import { SelectionModel } from '@angular/cdk/collections';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatCheckbox } from '@angular/material/material';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IPackageInfo,
    IProcess,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    LibraryService,
    ModalService,
    ODataQueryOptions,
    PackageService,
    ProcessService,
} from '@services';

import {
    BehaviorSubject,
    Subject,
} from 'rxjs';
import {
    filter,
    finalize,
    takeUntil,
} from 'rxjs/operators';

enum ProcessVersionViewMode {
    Processes,
    ReleaseNotes,
}

interface IPackageListItem {
    id: string;
    version: string;
    processes: IProcess[];
}

@Component({
    templateUrl: './nuget-upgrade.modal.html',
    styleUrls: [
        './nuget-upgrade.modal.scss',
    ],
    encapsulation: ViewEncapsulation.None,
    providers: [
        LibraryService,
        PackageService,
        ProcessService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NugetUpgradeModalComponent
    extends UiModalComponent<IProcess>
    implements OnInit, OnDestroy {

    public ProcessVersionViewMode = ProcessVersionViewMode;
    public currentProcessVersionViewMode!: ProcessVersionViewMode;
    public processService = this._injector.get<ProcessService>(ProcessService);

    public loading$ = new BehaviorSubject<boolean>(true);
    public submitting$ = new BehaviorSubject<boolean>(false);
    public packageList!: IPackageListItem[];
    public selection = new SelectionModel<IProcess>(true);
    public totalPackagesToUpgrade = 0;

    private _destroyed$ = new Subject();
    private _modalService = this._injector.get<ModalService>(ModalService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);

    ngOnInit() {
        this.packageList = this.context.additionalData.packages.map((p: IPackageInfo) => ({
            id: p.id,
            version: p.version,
            processes: [] as IProcess[],
        }) || [] as IPackageListItem[]);

        const filterOptions = this.context.additionalData.packages.map((p: IPackageInfo) => ({
            ProcessKey: p.id,
        }) || [] as { ProcessKey: string }[]);

        const queryOptions: ODataQueryOptions = {
            orderBy: ['ProcessVersion desc'],
            filter: {
                or: filterOptions,
            },
        };

        this.processService
            .get(queryOptions)
            .pipe(
                finalize(() => this.loading$.next(false)),
                filter(({ data }) => !!data),
                takeUntil(this._destroyed$),
            )
            .subscribe(({ data }) => data.forEach((process) => {
                const pkg = this._getPackage(process.processKey);

                if (!pkg) { return; }

                this.totalPackagesToUpgrade += 1;
                pkg.processes.push(process);
            }));
    }

    ngOnDestroy() {
        this.loading$.complete();
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public checkedAll(packageId: string): boolean {
        const pkg = this._getPackage(packageId)!;
        return pkg.processes.every(p => this.selection.isSelected(p));
    }

    public indeterminate(packageId: string): boolean {
        const pkg = this._getPackage(packageId)!;
        return pkg.processes.some(p => this.selection.isSelected(p)) &&
            !pkg.processes.every(p => this.selection.isSelected(p));
    }

    public toggleAll(checkbox: MatCheckbox, packageId: string) {
        const pkg = this._getPackage(packageId)!;
        checkbox.checked ? this.selection.select(...pkg.processes)
            : this.selection.deselect(...pkg.processes);
    }

    public onUpgradeVersion() {
        if (this.selection.isEmpty()) { return; }

        const promptMessage = this._translateService.instant('package_upgrade_upgrade_confirmation');

        this._modalService.onConfirm(null, () => {
            this.submitting$.next(true);
            const selectedKeys = this.selection.selected.map(r => r.id);
            this.processService.updateReleasesToLatestVersionBulk(selectedKeys)
                .pipe(
                    finalize(() => {
                        this.submitting$.next(false);
                    }),
                )
                .subscribe(() => {
                    this._dialogRef.close({
                        type: ModalResultType.Update,
                    });
                });

        }, () => { }, promptMessage);
    }

    public processLabel = (process: IProcess) =>
        `v${process.processVersion} – ${process.environmentName}`

    private _getPackage = (id: string) =>
        this.packageList.find(packageItem => packageItem.id === id)
}
